import "./upload-page.css";

import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Progress,
  Tooltip,
  Upload,
  UploadFile,
} from "antd";
import { FolderOutlined, UploadOutlined } from "@ant-design/icons";
import {
  ContainerData,
  ContainerFolderStructure,
  ContainersList,
  FailureFileTrackData,
  FileTrackData,
  FolderStructureParamsDictionary,
  MetadataItem,
  MetadataRowItem,
  UploadProgressEvent,
} from "../../api/data-types";

import {
  filterOnlyMetadataItems,
  filterOnlyProfileItems,
  selectFolderTooltipConditions,
  submitButtonTooltipConditions,
  uploadAFileWithMetadataFile,
} from "./util/upload-page-util";

import {
  getContainerData,
  getContainerList,
} from "../browse-page/util/browse-page-util";
import { MetadataTable } from "./sub-components/metadata-table";
import { DropdownListSelection } from "../layout/list-selection-dropdown/list-selection-dropdown";
import { FolderModal } from "../layout/folder-modal/folder-modal";
import { UserBrowsingSessionProps } from "../../App";

import { EMPTY_DICTIONARY } from "./util/data-types/data-types-upload-page";

import { store } from "@gsk-tech/gsk-react-framework";

import { Overlay } from "../overlay/overlay";
import { useNavigate } from "react-router-dom";
import {
  useAppInsightsContext,
  useTrackEvent,
} from "@microsoft/applicationinsights-react-js";
import { isLocalHost } from "../../util/helpers";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { ClientApiDiagnosticEvents } from "../../util/diagnosticEvent";

import { RcFile } from "antd/es/upload";

export const UploadPage = ({
  theme,
  containerState,
  updateContainerState,
}: UserBrowsingSessionProps) => {
  const navigate = useNavigate();
  const [containerData, setContainerData] = useState<ContainersList>({
    containers: [],
  });

  const [selectedContainer, setSelectedContainer] = useState<ContainerData>(
    new ContainerData("", "", [])
  );

  const updateContainer = async (newContainer: string) => {
    const container = await getContainerData(newContainer);
    setSelectedContainer(container);
    updateContainerState(newContainer);
    resetMetadata(container);
    return container;
  };

  const [folderStructureDependencies, setFolderStructureDependencies] =
    useState<FolderStructureParamsDictionary>(EMPTY_DICTIONARY);

  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [loading, setUploading] = useState<boolean>(false);

  const [currentFileName, setCurrentFileName] = useState<string | undefined>();

  const [uploadDone, setUploadDone] = useState<boolean>(false);

  const [errorCheck, setErrorCheck] = useState<boolean>(false);

  const [metadataCheck, setMetadataCheck] = useState<boolean>(true);

  const [folderCheck, setFolderCheck] = useState<boolean>(true);

  const [metadataItems, setMetadataItems] = useState<MetadataRowItem[]>([]);

  const [countMetadata, setCountMetadata] = useState<number>(0);

  const [percentDone, setPercentDone] = useState<number>(0);

  const [avgSpeed, setAvgSpeed] = useState<number | undefined>();

  const [failedFiles, setFailedFiles] = useState<string[]>([]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const [containerWriteAccess, setContainerWriteAccess] =
    useState<boolean>(true);

  const [isFolderSelectModalOpen, setIsFolderSelectModalOpen] =
    useState<boolean>(false);

  const [folderPath, setFolderPath] = useState<string>("");

  const [folderChecked, setFolderChecked] = useState<boolean>(false);

  let totalFileSize: number = 0;
  let previousFilesSize: number = 0;

  const onFolderChange = (e: CheckboxChangeEvent) => {
    setFolderChecked(e.target.checked);
  };

  const mudid = store.getState()?.loggedInUser?.mudid;
  const resetAfterUpload = () => {
    setUploadDone(false);
    setErrorCheck(false);
    setPercentDone(0);
    setFailedFiles([]);
    setMetadataCheck(true);
    setFolderCheck(true);
  };

  const onProgress = (ev: UploadProgressEvent) => {
    setPercentDone(
      Math.round(
        ((previousFilesSize + ev.dataAlreadyUploaded + ev.loaded) /
          totalFileSize) *
        100
      )
    );
    if (avgSpeed !== null) setAvgSpeed(ev.rate!);
    setCurrentFileName(ev.fileName);
  };

  const updateFolderValue = (aPath: string, updatePath: boolean) => {
    setIsFolderSelectModalOpen(false);
    if (updatePath) {
      setFolderPath(aPath);
    }
  };

  const handleContainersDropdownMenuClick = async (containerName: string) => {
    const container = await updateContainer(containerName);
    const dictionary =
      container.folderStructure?.getFolderStructureParamsDictionary([]);
    setFolderStructureDependencies(dictionary ? dictionary : EMPTY_DICTIONARY);
    return container;
  };

  const handleChooseFile = (file: any) => {
    setFileList((prevFileList) => [...prevFileList, file]);
    return false;
  };

  const handleAddMetadataItem = () => {
    setMetadataItems([
      ...metadataItems,
      new MetadataRowItem("", "", countMetadata),
    ]);
    setCountMetadata(countMetadata + 1);
  };

  const handleAddMetadataTemplate = (file: File) => {
    let tmpMetadataTable: MetadataRowItem[] = [];
    let counter: number = 1;

    const reader = new FileReader();
    reader.onload = (e) => {
      const fileAsString = e.target?.result as string;
      if (fileAsString !== null) {
        try {
          const JSONfile = JSON.parse(fileAsString);
          for (const [key, value] of Object.entries(JSONfile)) {
            if (typeof value === "string") {
              tmpMetadataTable = [
                ...tmpMetadataTable,
                new MetadataRowItem(key, value, countMetadata + counter),
              ];
              counter += 1;
            } else {
              tmpMetadataTable = [
                ...tmpMetadataTable,
                new MetadataRowItem(key, "", countMetadata + counter),
              ];
              counter += 1;
            }
          }
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          } else {
            setErrorMessage("An unexpected error occurred.");
          }
        }
        for (let i = 0; i < selectedContainer.metadataTemplate.length; i++) {
          let index = tmpMetadataTable
            .map((item) => item.item.key)
            .indexOf(selectedContainer.metadataTemplate[i].key);
          if (index !== -1) {
            tmpMetadataTable[index].itemTemplate =
              selectedContainer.metadataTemplate[i];
            if (tmpMetadataTable[index].itemTemplate.readOnly) {
              tmpMetadataTable[index].item.value =
                tmpMetadataTable[index].itemTemplate.defaultValue;
            }
          } else {
            if (selectedContainer.metadataTemplate[i].required) {
              tmpMetadataTable = [
                ...tmpMetadataTable,
                new MetadataRowItem(
                  selectedContainer.metadataTemplate[i].key,
                  selectedContainer.metadataTemplate[i].defaultValue,
                  countMetadata + counter,
                  selectedContainer.metadataTemplate[i].required,
                  selectedContainer.metadataTemplate[i].readOnly,
                  selectedContainer.metadataTemplate[i].dataType,
                  true
                ),
              ];
              counter += 1;
            }
          }
        }
        setMetadataItems(tmpMetadataTable);
        setCountMetadata(countMetadata + counter);
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleMetadatatemplateError = () => {
    resetAfterUpload();
    setErrorMessage(undefined);
    form.resetFields();
    resetMetadata(selectedContainer);
  };

  const handleRemoveMetadataItem = (key: number) => {
    const updatedData = metadataItems.filter(
      (item) => metadataItems.indexOf(item) !== key
    );
    setMetadataItems(updatedData);
  };

  const handleItemChange = (key: number, metadataItem: MetadataRowItem) => {
    let tmpMetadataItems: MetadataRowItem[] = Object.assign([], metadataItems);
    tmpMetadataItems[key] = metadataItem;
    setMetadataItems(tmpMetadataItems);
  };

  const handleOkUpload = () => {
    resetAfterUpload();
    setFileList([]);
    form.resetFields();
    resetMetadata(selectedContainer);
  };

  const goToFolderClick = () => {
    const navigateSearchParams = new URLSearchParams();
    navigateSearchParams.set("container", selectedContainer.containerName);
    navigateSearchParams.set("folder", folderPath);
    navigate({
      pathname: "../browse",
      search: navigateSearchParams.toString(),
    });
    handleOkUpload();
  };

  const handleMetadataCheckError = () => {
    resetAfterUpload();
  };

  const handleRemoveFileUpload = (file: UploadFile) => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  const resetMetadata = (container: ContainerData) => {
    let metadataFromTemplate: MetadataRowItem[] = [];
    for (let i = 0; i < container.metadataTemplate.length; i++) {
      metadataFromTemplate = [
        ...metadataFromTemplate,
        new MetadataRowItem(
          container.metadataTemplate[i].key,
          container.metadataTemplate[i].defaultValue,
          countMetadata + i,
          container.metadataTemplate[i].required,
          container.metadataTemplate[i].readOnly,
          container.metadataTemplate[i].dataType,
          container.metadataTemplate[i].fromConfiguration
        ),
      ];
    }
    setCountMetadata(countMetadata + container.metadataTemplate.length);
    setMetadataItems(metadataFromTemplate);
  };

  const validateFolderPattern = (): boolean => {
    let pathElements = folderPath.split("/");
    let check = true;
    let index = 0;
    if (folderStructureDependencies.isDictionaryComplete) {
      Object.entries(folderStructureDependencies.entries).forEach(
        ([key, value]) => {
          if (pathElements[index] !== value) {
            check = false;
          }
          index++;
        }
      );
    } else {
      check = false;
    }
    return check;
  };

  //on component mounting do:
  useEffect(() => {
    getContainerList("Write")
      .then((containersList) => {
        const containersData: ContainerData[] = containersList.map((name) => ({
          containerName: name,
          folderStructure: new ContainerFolderStructure(""),
          metadataTemplate: [],
        }));
        setContainerData((prevData) => ({
          ...prevData,
          containers: containersData,
        }));
        return containersList;
      })
      .then((containersList) => {
        //localStorage has priority over first container from the list, overriden if null
        if (containersList.length === 0) {
          setContainerWriteAccess(false);
          return new ContainerData("", "", [])
        }
        else
        {
          const container = containerState ? containerState : containersList[0];
          return handleContainersDropdownMenuClick(container);
        }
      })
      .then((container) => {
        resetMetadata(container);
      })
      .catch((error) => {
        console.error("Error on mount:", error.message);
      });
  }, []);

  const [form] = Form.useForm();
  const appInsights = useAppInsightsContext();
  const trackUploadEvent = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[ClientApiDiagnosticEvents.ClientUploadFile],
    {} as FileTrackData
  );
  const trackUploadFailureEvent = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[
      ClientApiDiagnosticEvents.ClientUploadFileFailure
    ],
    {} as FailureFileTrackData
  );

  async function submitUploadAction(inputValues: any) {
    let tmpMetadataItems: MetadataItem[] = [];
    let metadataCheck: boolean = true;
    let folderCheck: boolean = validateFolderPattern();
    let failedFiles: string[] = [];
    setMetadataCheck(true);
    if (metadataItems.length === 0) {
      metadataCheck = false;
    }
    for (let i = 0; i < metadataItems.length; i++) {
      let item: MetadataItem = metadataItems[i].item;
      if (
        metadataItems[i].itemTemplate.fromConfiguration &&
        metadataItems[i].itemTemplate.readOnly
      ) {
        item.value = metadataItems[i].itemTemplate.defaultValue;
      }
      if (item.key === "" || item.key.trim() === "") {
        if (item.value !== "" && item.value.trim() !== "") {
          metadataCheck = false;
          break;
        }
      } else {
        if (item.value === "" || item.value.trim() === "") {
          if (metadataItems[i].itemTemplate.required) {
            if (metadataItems[i].itemTemplate.defaultValue !== "") {
              item.value = metadataItems[i].itemTemplate.defaultValue;
              tmpMetadataItems = [...tmpMetadataItems, item];
            } else {
              metadataCheck = false;
              break;
            }
          }
        } else {
          tmpMetadataItems = [...tmpMetadataItems, item];
        }
      }
    }
    if (!metadataCheck) {
      setMetadataCheck(false);
    } else if (!folderCheck) {
      setFolderCheck(false);
    } else {
      let folderCheckedBefore = folderChecked;
      if (!folderChecked) {
        for (let index = 0; index < fileList.length; index++) {
          const file = fileList[index] as RcFile;
          if (file.webkitRelativePath !== undefined && file.webkitRelativePath !== "") {
            folderCheckedBefore = true;
            break;
          }
        }
      }
      for (let i = 0; i < fileList.length; i++) {
        totalFileSize += fileList[i].size!;
      }
      setMetadataCheck(true);
      setUploading(true);
      setUploadDone(false);
      setErrorCheck(false);
      setPercentDone(0);
      for (let i = 0; i < fileList.length; i++) {
        const start = new Date();
        let chosenFolder = inputValues.folder;
        let fileName: string | undefined = undefined;
        if (folderChecked || folderCheckedBefore) {
          let fileURL = (fileList[i] as RcFile).webkitRelativePath;
          if (fileURL && fileURL.length !== 0) {
            chosenFolder += "/";
            chosenFolder += fileURL.substring(0, fileURL.lastIndexOf("/"));
            fileName = fileURL.substring(fileURL.lastIndexOf("/") + 1);
          }
        }
        const status = await uploadAFileWithMetadataFile(
          selectedContainer.containerName,
          fileList[i],
          tmpMetadataItems,
          onProgress,
          chosenFolder,
          fileName
        );
        if (!isLocalHost()) {
          const end = new Date();
          const totalTime = end.getTime() - start.getTime();
          const fileTracked = {
            totalTime,
            containerName: selectedContainer.containerName,
            folderName: inputValues.folder as string,
            fileName: fileList[i].name,
            fileGuid: fileList[i].uid,
            fileSize: fileList[i].size!,
          };
          if (status !== 200) {
            trackUploadFailureEvent({ ...fileTracked, error: status });
          } else {
            trackUploadEvent(fileTracked);
          }
        }
        previousFilesSize += fileList[i].size!;
        if (status !== 200) {
          let fileName = fileList[i].name;
          let fileNameAdded = `${fileName}`;
          failedFiles = [...failedFiles, fileNameAdded];
        }
      }
      setAvgSpeed(undefined);
      setUploading(false);
      if (failedFiles.length !== 0) {
        setFailedFiles(failedFiles);
        setErrorCheck(true);
      } else {
        setPercentDone(100);
        setUploadDone(true);
      }
    }
  }

  const onReset = () => {
    form.resetFields();
    setFileList([]);
    resetMetadata(selectedContainer);
    setFolderPath("");
  };

  useEffect(() => {
    form.setFieldValue("folder", folderPath);
  }, [folderPath]);

  useEffect(() => {
    const dependencies = folderStructureDependencies.getAllKeys();
    const profileDependencies = filterOnlyProfileItems(dependencies);
    const metadataDependencies = filterOnlyMetadataItems(dependencies);
    const avaiableMetadataKeys = metadataItems.map((metadataItem) =>
      metadataItem.item.key.toLowerCase()
    );
    profileDependencies.forEach((key) => {
      if (key === "mudid") {
        folderStructureDependencies.setValue(`%profile.${key}%`, mudid);
      }
    });
    avaiableMetadataKeys.forEach((key) => {
      if (metadataDependencies.includes(key)) {
        const index = metadataItems
          .map((entry) => entry.item.key.toLowerCase())
          .indexOf(key);
        folderStructureDependencies.setValue(
          `%metadata['${key}']%`,
          metadataItems[index].item.value
        );
      }
    });
    setFolderPath(folderStructureDependencies.getCompletedFolderPattern());
  }, [selectedContainer, metadataItems, folderStructureDependencies, mudid]);

  const textColor = theme === "light" ? "#000000" : "#ffffff";

  return (
    <div className="uploadPageDiv">
      {!containerWriteAccess && (
        <Overlay
          noAccesText="You are not allowed to upload files to any container"
        />
      )}
      <div className="stripeDiv">
        <div className="gsk-sub-header-menu">
          <Col className="containerText">Container :</Col>
          <Col flex={1} className="containerDropdown">
            <DropdownListSelection
              theme={theme}
              optionsList={containerData.containers.map(
                (containerData) => containerData.containerName
              )}
              onMenuClick={handleContainersDropdownMenuClick}
              placeholderText={selectedContainer.containerName}
            />
          </Col>
          <Col className="metadataTemColumn">
            <Upload
              accept=".json"
              beforeUpload={handleAddMetadataTemplate}
              showUploadList={false}
            >
              <Button
                className="metadataTemplateButton"
                loading={loading}
                disabled={!containerWriteAccess}
              >
                Load metadata
              </Button>
            </Upload>
            <Modal
              title="Error!"
              onOk={handleMetadatatemplateError}
              okButtonProps={{ style: { color: "black" } }}
              cancelButtonProps={{ style: { display: "none" } }}
              closable={false}
              open={errorMessage !== undefined}
            >
              <p style={{ color: textColor }}>`{errorMessage}`</p>
            </Modal>
          </Col>
        </div>
      </div>
      <Form
        className="uploadForm"
        form={form}
        name="control-hooks"
        onFinish={submitUploadAction}
      >
        <div className="uploadCard">
          <div className="uploadFormCol">
            <div className="row">
              <div className="labelBox">
                <span>Destination Folder</span>
              </div>
              <div className="inputBox">
                <Form.Item name="folder">
                  <Input
                    placeholder="Placeholder"
                    style={{ display: "flex", top: "10px", color: textColor }}
                    disabled={true}
                  />
                </Form.Item>
              </div>
              <div className="buttonBox">
                <Tooltip
                  title={selectFolderTooltipConditions(
                    selectedContainer.containerName,
                    folderStructureDependencies.isDictionaryComplete
                  )}
                >
                  <Button
                    className="folderButton"
                    icon={<FolderOutlined />}
                    onClick={() => {
                      setIsFolderSelectModalOpen(() => true);
                    }}
                    disabled={
                      selectedContainer.containerName ===
                        "" ||
                      !folderStructureDependencies.isDictionaryComplete
                    }
                  />
                  <FolderModal
                    theme={theme}
                    isOpen={isFolderSelectModalOpen}
                    containerName={selectedContainer.containerName}
                    rootFolderPath={folderPath}
                    sendPathToParent={updateFolderValue}
                    showInput={true}
                    numberOfDisabledInPath={
                      folderStructureDependencies.getAllKeys().length
                    }
                  />
                </Tooltip>
              </div>
            </div>
            <div className="row" style={{ flex: "1" }}>
              <div className="labelBox">
                <span style={{ color: "red" }}>*</span>
                <span>Select Files</span>
              </div>
              <div className="inputBox">
                <Upload
                  beforeUpload={handleChooseFile}
                  fileList={fileList}
                  onRemove={handleRemoveFileUpload}
                  multiple={true}
                  directory={folderChecked ? true : false}
                >
                  <Button icon={<UploadOutlined />} className="selectionButton">
                    {folderChecked ? "Select folder" : "Select files"}
                  </Button>
                </Upload>
                <Modal
                  title="Error!"
                  onOk={handleMetadataCheckError}
                  cancelButtonProps={{ style: { display: "none" } }}
                  closable={false}
                  open={!metadataCheck}
                >
                  <p style={{ color: textColor }}>
                    Please make sure that every required key has a value, there
                    are no values without provided keys and that you have at
                    least one key/value pair.
                  </p>
                </Modal>
                <Modal
                  title="Error!"
                  onOk={handleMetadataCheckError}
                  cancelButtonProps={{ style: { display: "none" } }}
                  closable={false}
                  open={!folderCheck}
                >
                  <p style={{ color: textColor }}>
                    Please make sure that folder pattern is correct.
                  </p>
                </Modal>
                <Modal
                  title="Upload finished successfully"
                  open={uploadDone}
                  footer={[
                    <Button
                      key="submit"
                      type="default"
                      onClick={handleOkUpload}
                    >
                      Close
                    </Button>,
                    <Button
                      key="submit"
                      type="primary"
                      onClick={goToFolderClick}
                    >
                      Open folder
                    </Button>,
                  ]}
                  closable={false}
                >
                  <Progress
                    percent={percentDone}
                    size={[400, 15]}
                    className="percentage"
                  />
                </Modal>
                <Modal
                  title="Error!"
                  onOk={handleOkUpload}
                  cancelButtonProps={{ style: { display: "none" } }}
                  closable={false}
                  open={errorCheck}
                  style={{ overflow: "auto" }}
                >
                  <div style={{ color: textColor }}>
                    There has been an error in file upload.<br></br>
                    Error in uploading: <br></br>
                    {failedFiles.map((item, index) => (
                      <div key={index}>{item}</div>
                    ))}
                  </div>
                </Modal>
                <Modal
                  title="Uploading in progress..."
                  footer={null}
                  closable={false}
                  open={loading}
                >
                  <div>
                    <Progress
                      percent={Math.round(percentDone)}
                      status="active"
                      size={[400, 15]}
                    />
                    <br />
                    {Math.round(percentDone) !== 100 ? (
                      <div>
                        <span
                          style={{ color: textColor }}
                          className="currFileName"
                        >
                          {currentFileName}
                        </span>
                        <span
                          style={{ color: textColor }}
                          className="avgSpeedSpan"
                        >
                          {avgSpeed === undefined
                            ? ""
                            : (avgSpeed / ((1024 * 1024) as number)).toFixed(
                                2
                              ) + "MBytes/s"}
                        </span>
                      </div>
                    ) : (
                      <span
                        style={{ color: textColor }}
                        className="currFileName"
                      >
                        Finalising the upload
                      </span>
                    )}
                  </div>
                </Modal>
              </div>
              <div className="checkbox">
                <Checkbox
                  className="checkboxFolder"
                  checked={folderChecked}
                  onChange={onFolderChange}
                >
                  Select folder
                </Checkbox>
              </div>
            </div>
            <div className="row">
              <div className="labelBox"></div>
              <div className="inputBoxButtons">
                <Form.Item>
                  <Button
                    htmlType="button"
                    onClick={onReset}
                    className="resetButton"
                  >
                    Reset
                  </Button>
                </Form.Item>
                <Form.Item>
                  <Tooltip
                    title={submitButtonTooltipConditions(
                      fileList.length,
                      selectedContainer.containerName,
                      folderStructureDependencies.isDictionaryComplete
                    )}
                  >
                    <Button
                      loading={loading}
                      type="primary"
                      htmlType="submit"
                      className="submitButton"
                      disabled={
                        fileList.length === 0 ||
                        selectedContainer.containerName === "" ||
                        !folderStructureDependencies.isDictionaryComplete ||
                        loading
                      }
                    >
                      {loading ? "Uploading" : "Upload"}
                    </Button>
                  </Tooltip>
                </Form.Item>
              </div>
            </div>
          </div>
          <div className="metadataCol">
            <div className="metadataTable">
              <MetadataTable
                isModal={false}
                theme={theme}
                metadataItems={metadataItems}
                handleRemoveMetadataItem={handleRemoveMetadataItem}
                handleItemChange={handleItemChange}
                isMassUpdate={false}
                selectedContainer={selectedContainer}
              />
            </div>
            <div className="addButtonColumn">
              <Button
                className="addButton"
                onClick={handleAddMetadataItem}
                disabled={!containerWriteAccess}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>


  );
};
